import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageMeta } from "@/components/site/PageMeta";
import { AssessmentShell, ChoiceQuestion } from "@/components/built-index/AssessmentShell";
import { BUILT_INDEX_ROUTES, DISC_QUESTIONS } from "@/content/built-index";
import { readSession, writeSession } from "@/lib/built-index-session";
import { trackBuiltIndexStep } from "@/lib/built-index-analytics";

const BuiltIndexDisc = () => {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState<Record<string, string>>({});

  useEffect(() => {
    const s = readSession();
    if (!s.id) {
      navigate(BUILT_INDEX_ROUTES.start, { replace: true });
      return;
    }
    setAnswers(s.disc);
  }, [navigate]);

  const set = (id: string, value: string) => {
    const next = { ...answers, [id]: value };
    setAnswers(next);
    writeSession({ disc: next });
  };

  const answered = DISC_QUESTIONS.filter((q) => answers[q.id]).length;

  return (
    <>
      <PageMeta
        title="Leadership Style · The Built Index"
        description="Identify how you decide, drive and delegate under pressure."
        path="/built-index/disc"
        noindex
      />
      <AssessmentShell
        step={2}
        title="Your Leadership Style"
        intro="Choose the option that describes what you actually do under pressure — not the version you would like to be true."
        footer={
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <span className="text-silver text-xs uppercase tracking-[0.2em]">
              {answered} of {DISC_QUESTIONS.length} answered
            </span>
            <button
              type="button"
              disabled={answered !== DISC_QUESTIONS.length}
              onClick={() => {
                trackBuiltIndexStep("assessment_disc", {
                  sessionId: readSession().id,
                  email: readSession().email,
                  metadata: { answered },
                });
                navigate(BUILT_INDEX_ROUTES.love);
              }}
              className="inline-flex items-center gap-2 bg-orange text-primary-foreground px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:brightness-110 transition disabled:opacity-50"
            >
              Continue to Fuel &amp; Drain <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        }
      >
        {DISC_QUESTIONS.map((q, i) => (
          <ChoiceQuestion
            key={q.id}
            id={q.id}
            index={i + 1}
            prompt={q.prompt}
            options={q.options as unknown as Record<string, string>}
            value={answers[q.id]}
            onChange={(v) => set(q.id, v)}
          />
        ))}
      </AssessmentShell>
    </>
  );
};

export default BuiltIndexDisc;
